import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, SimpleSelect, StatusBadge, Table, Th, Td, Tr, Empty, ExportButton } from "@/components/common";
import { Belt } from "@/components/Belt";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { StudentForm } from "./StudentForm";
import { brl, dt } from "@/lib/fmt";

const STATUSES = ["all", "ativo", "experimental", "inativo", "suspenso", "cancelado"];

export default function Students() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [open, setOpen] = useState(false);
  const qs = new URLSearchParams({ q, ...(status !== "all" && { status }), ...(plan !== "all" && { plan_id: plan }) }).toString();
  const [rows, reload] = useApi(`/students?${qs}`);
  const [plans] = useApi("/plans");
  const [classes] = useApi("/classes");
  const [cfg] = useApi("/graduation/config");
  const isAdmin = user.role === "admin";
  const belts = cfg ? [...new Set([...cfg.adult_belts, ...cfg.kids_belts])] : ["Branca"];

  return (
    <div>
      <PageHeader eyebrow="Cadastro" title="Alunos">
        {isAdmin && <ExportButton kind="alunos" />}
        {isAdmin && <Button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="new-student-btn"><Plus className="w-4 h-4 mr-1" /> Nova matrícula</Button>}
      </PageHeader>
      <div className="panel">
        <div className="flex flex-col md:flex-row gap-3 p-4 border-b border-slate-100">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF ou plano" className="pl-9" data-testid="student-search-input" />
          </div>
          <SimpleSelect value={status} onChange={setStatus} className="md:w-44" testid="student-status-filter"
            options={STATUSES.map((s) => ({ value: s, label: s === "all" ? "Todos os status" : s[0].toUpperCase() + s.slice(1) }))} />
          <SimpleSelect value={plan} onChange={setPlan} className="md:w-52" testid="student-plan-filter"
            options={[{ value: "all", label: "Todos os planos" }, ...(plans || []).map((p) => ({ value: p.id, label: p.name }))]} />
        </div>
        {!rows ? <Empty text="Carregando…" /> : rows.length === 0 ? <Empty /> : (
          <Table testid="students-table">
            <thead><tr><Th>Aluno</Th><Th>CPF</Th><Th>Faixa</Th><Th>Plano</Th>{isAdmin && <Th>Mensalidade</Th>}<Th>Vence</Th><Th>Status</Th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <Tr key={s.id} onClick={() => nav(`/admin/alunos/${s.id}`)} testid={`student-row-${s.id}`}>
                  <Td><div className="font-semibold">{s.full_name}</div><div className="text-xs text-slate-500">{s.age} anos {s.is_minor && "· menor"}</div></Td>
                  <Td className="font-mono text-xs">{s.cpf}</Td>
                  <Td><Belt belt={s.belt} grau={s.grau} size="sm" /></Td>
                  <Td>{s.plan_name || "—"}</Td>
                  {isAdmin && <Td>{brl(s.monthly_fee)}</Td>}
                  <Td className={s.plan_end && s.plan_end < new Date().toISOString().slice(0, 10) ? "text-red-600 font-semibold" : ""}>{dt(s.plan_end)}</Td>
                  <Td><StatusBadge status={s.status} /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      {open && <StudentForm open={open} onClose={() => setOpen(false)} plans={(plans || []).filter((p) => p.active)} classes={classes || []} belts={belts}
        onSaved={(s) => { reload(); nav(`/admin/alunos/${s.id}`); }} />}
    </div>
  );
}
